import { graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import FrenchMade from "../assets/icons/fabrique_en_france.svg";
import ImpressionFrancais from "../assets/icons/impression-francaise-small.svg";
import Livraison7 from "../assets/icons/livraison-7-jours-sm.svg";
import LivraisonOfferte from "../assets/icons/livraison-offerte-sm.svg";
import Breadcrumbs from "../components/breadcrumbs/breadcrumbs";
import Layout from "../components/layout";
import ProductList from "../components/product-list/product-list";
import { productAlt } from "../utils/product-alt";
import SeparatorComponent from "../components/separator/separator";
import OekoTexSvg from "../assets/icons/oeko-tex.svg";


import FAQProductView from "../components/faq-product";
import faqProduits from "../data/faq-produits.json";
// Bloc rédactionnel sous le tableau « Description », par slug produit (TTBFR-35).
import descriptionsProduits from "../data/descriptions-produits.json";
import SkeepersWidget from "../components/skeeper-widget";
import { addCartItem } from "../utils/cart";
// import Devis24h from "../assets/icons/devis-sous-24_H.svg"
// import Livraison7 from "../assets/icons/livraison.svg"
// import LivraisonOfferte from "../assets/icons/livraison-offerte.svg"

export const query = graphql`
  query ProductQuery($slug: String!) {
    check: file( relativePath: { eq: "check-1.png" } ) {
      childImageSharp {
        gatsbyImageData(width: 17) 
      }
    }
    iconline1: file( relativePath: { eq: "devis-en-24h pour votre totebag.png" } ) {
      childImageSharp {
        gatsbyImageData(width: 64, height: 64) 
      }
    }
    iconline2: file( relativePath: { eq: "livraison 7 jours.png" } ) {
      childImageSharp {
        gatsbyImageData(width: 64, height: 64) 
      }
    }
    iconline3: file( relativePath: { eq: "vos totebags livrés gratuitement.png" } ) {
      childImageSharp {
        gatsbyImageData(width: 64, height: 64) 
      }
    }
    
    strapiProduct(slug: { eq: $slug }) {
      description
      id
      price
      short_description
      slug
      status
      title
      metatitle
      metadescription
      image {
        localFile {
          childImageSharp {
            gatsbyImageData(width: 600) 
          }
        }
      }
      variations {
        attributs_couleur {
          Nom
          valeur
          id
        }
        no_stock
        id
        Image {
          localFile {
            childImageSharp {
              gatsbyImageData(width: 600) 
            }
          }
        }
      }
      attributs_quantites {
        Nom
        id
      }
      attributs_personnalisations {
        Nom
        id
      }
      attributs_marquages {
        Nom
        id
      }
      attributs_compositions {
        Nom
        id
      }
      attributs_anses {
        Nom
        id
      }
      attributs_grammages {
        Nom
        id
      }
      attributs_labels {
        Label
        id
      }
      attributs_dimensions {
        Dimension
        id
      }
      made_in_france
      best_seller
      zone_image {
        localFile {
          publicURL
          childImageSharp {
            gatsbyImageData(width: 800)
          }
        }
      }
      attributs_zone {
        Name
        id
      }
      categories {
        name
        parent
        slug
        id
      }
    }
    allStrapiProduct(
      filter: { slug: { ne: $slug }, status: { eq: "published" },categories: { elemMatch: { slug: { eq: "tote-bags-personnalises" } } } }
      limit: 4
    ) {
      edges {
        node {
          slug
          status
          strapiId
          title
          categories {
            name
            id
            slug
          }
          image {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 230, height: 280) 
              }
            }
          }
        }
      }
    }
  }
`;

const Product = React.forwardRef(({ data }, ref) => {
  const [quantity, setQuantity] = useState("100");
  const [activeTab, setActiveTab] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [color, setColor] = useState(null);
  const [noStock, setNoStock] = useState(false);
  const [faces, setFaces] = useState(1);
  const [marquage, setMarquage] = useState(null);
  const [cartItem, setCartItem] = useState(null);
  const [showCall, setShowCall] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showError, setShowError] = useState(false);
  const [showZoneImageModal, setShowZoneImageModal] = useState(false);
  const [utmParams, setUTMParams] = useState({});
  const { utm_campaign, utm_term, gad_source } = utmParams;

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      // Récupérer depuis dataLayer
      const storedUTM = window.dataLayer.find(item => item.event === "utm_params_received");
      if (storedUTM) {
        setUTMParams(storedUTM.utm_params);
      }
    }
  }, [setUTMParams]);

  const product = data.strapiProduct;
  const products = data.allStrapiProduct.edges.map((e) => e.node);
  const category =
    product.categories && product.categories.length && product.categories[0];
  const siteUrl = "https://latelierdutotebag.com";
  // Un produit sans catégorie vit à la racine (cf. le calcul de l'URL dans
  // node-product.js) : sans ce test on émettrait une URL à double slash.
  const productUrl = category?.slug
    ? `${siteUrl}/${category.slug}/${product.slug}`
    : `${siteUrl}/${product.slug}`;
  const productImage = product.image.localFile.childImageSharp.gatsbyImageData.images.fallback.src;
  const productImageUrl = /^https?:\/\//.test(productImage) ? productImage : `${siteUrl}${productImage}`;
  const questionProduit = faqProduits[product.slug];
  const descriptionProduit = descriptionsProduits[product.slug];
  const seo = {
    metaTitle: product.metatitle || product.title,
    metaDescription: product.metadescription || product.description,
    shareImage: productImage,
    article: false,
    scripts: [
      {
        type: "application/ld+json",
        content: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "@id": `${productUrl}#product`,
          "name": product.title,
          "description": product.description,
          "image": productImageUrl,
          "url": productUrl,
          "mainEntityOfPage": productUrl,
          "countryOfAssembly": "France",
          "countryOfLastProcessing": "France",
          "brand": {
            "@type": "Organization",
            "@id": `${siteUrl}/#organization`,
            "name": "L'Atelier du Tote Bag"
          },
          ...(product.attributs_compositions && product.attributs_compositions.length > 0 && {
            "material": product.attributs_compositions.map(c => c.Nom).join(", ")
          }),
          ...(product.attributs_grammages && product.attributs_grammages.length > 0 && {
            "weight": {
              "@type": "QuantitativeValue",
              "value": product.attributs_grammages[0].Nom.replace(/[^0-9]/g, ''),
              "unitCode": "GRM",
              "unitText": "gr/m²"
            }
          }),
          ...(product.made_in_france && {
            "countryOfOrigin": {
              "@type": "Country",
              "name": "France"
            }
          }),
          ...(product.attributs_anses && product.attributs_anses.length > 0 && {
            "additionalProperty": [{
              "@type": "PropertyValue",
              "name": "Taille des anses",
              "value": product.attributs_anses[0].Nom
            }]
          }),
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "ratingCount": "495",
            "worstRating": "1",
            "bestRating": "5"
          }
        })
      },
      ...(questionProduit ? [{
        type: "application/ld+json",
        content: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${productUrl}#faq`,
          "mainEntity": [{
            "@type": "Question",
            "name": questionProduit.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": questionProduit.reponse
            }
          }]
        })
      }] : [])
    ]
  };

  React.useEffect(() => {
    if (cartItem) {
      setCartItem(null);
    }

    const variations = product.variations
      .filter((v) => v.attributs_couleur);
    if (variations.length === 1) {
      slideRef.current.slickGoTo(1);
      setColor(variations[0].attributs_couleur);
    }
  }, undefined);

  const addToCart = (e) => {
    e.preventDefault();
    if (typeof window === 'undefined') {
      return false;
    }
    const variation = product.variations
      .find((v) => v.attributs_couleur && color && v.attributs_couleur.Nom === color.Nom);

    const newCartItem = { color, marquage, quantity, product, variation, faces };
    addCartItem(newCartItem);
    setCartItem(newCartItem);

    return false;
  };

  const updateQty = (e) => {
    setQuantity(e.target.value);
  };

  const updateFaces = (e) => {
    setFaces(+e.target.value);
  };

  const showTab = (value) => {
    setActiveTab(value);
  };

  const quantities = ["100", "250", "500", "1000"];
  // const attributes = [
  //   ["Composition", "attributs_compositions"],
  //   // ['Couleurs', 'attributs_couleurs'],
  //   ["Personnalisation", "attributs_personnalisations", 1],
  //   ["Quantités", "attributs_quantites"],
  //   ["Anses", "attributs_anses"],
  //   ["Informations supplémentaires", "attributs_infos"],
  //   ["Marquages", "attributs_marquages", 1],
  // ];
  // const couleurs = product.variations.map((v) => v.attributs_couleur.Nom);

  const images = [
    { image: product.image, color: "", title: product.title, alt: productAlt(product.title) },
    ...product.variations
      .filter(v => {
        if(!v.Image)
          console.log(`${product.title} ${v.attributs_couleur.Nom}`)
        return !!v.Image
      })
      .map((v) => ({
        image: v.Image,
        color: v.attributs_couleur,
        title: `${product.title} ${v.attributs_couleur.Nom}`,
        alt: `${productAlt(product.title)} ${v.attributs_couleur.Nom.replace(/[-_]/g, " ").toLowerCase()}`,
      })),
  ];

  const settings = {
    dots: false,
    infinite: false,
    lazyLoad: true,
    speed: 500,
    adaptiveHeight: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (current, next) => setSlideIndex(next),
  };

  const settings2 = {
    dots: false,
    infinite: false,
    lazyLoad: true,
    speed: 500,
    adaptiveHeight: false,
    slidesToShow: 4,
    slidesToScroll: 1,
    // beforeChange: (current, next) => setSlideIndex(next)
  };

  const slideRef = React.useRef();
  const slideRef2 = React.useRef();

  const isPrintInFrance = (categoryId) => {
    const printInFranceCategories = [
      12, // totebag
      9, // trousse
      18, // pochette
      15, // sac coton bio
      16, // pochette bio
      19, // cabas
      28 // jute
    ];
    return printInFranceCategories.indexOf(categoryId) >= 0;
  }

  const listItems = (product.short_description || "").match(/[-*]\s+([^\n]+)/g) || [];
  const values = listItems.map(item => item.replace(/^[-*]\s+/, '').trim());

  // Les lignes de `short_description` sont assignées par DÉTECTION DE CONTENU (et non
  // par position) : leur ordre n'est pas garanti d'un produit à l'autre, ce qui décalait
  // l'affichage (ex. le label affiché sous « Anses » pour les pochettes sans anses).
  // Les lignes ne correspondant à aucun attribut connu sont regroupées dans « Autres
  // informations ». Une ligne absente ne crée plus de rangée vide.
  const detected = { dimensions: null, grammage: null, composition: null, anses: null, label: null };
  const autres = [];
  for (const v of values) {
    const s = v.toLowerCase();
    if (!detected.dimensions && /\d+\s*[x×]\s*\d+/.test(s)) detected.dimensions = v;
    else if (!detected.grammage && /g\s*\/\s*m|g\/m²|\bgrs?\b|grammes/.test(s)) detected.grammage = v;
    else if (!detected.anses && /anse|poign|boudin|tress|\blongues?\b|\bcourtes?\b/.test(s)) detected.anses = v;
    else if (!detected.composition && /coton|polyester|jute|jeans|twill|maille|toile|acrylique|%/.test(s)) detected.composition = v;
    else if (!detected.label && /oeko-?tex|gots/.test(s)) detected.label = v;
    else autres.push(v);
  }

  const hasBullets = values.length > 0;

  // Fallback structuré : pour les produits SANS short_description à puces, le tableau est
  // reconstruit depuis les attributs structurés (s'il y en a), avec reformatage (unité
  // « g/m² » sur le grammage stocké nu, « cm » sur les dimensions et sur les anses
  // numériques). Les produits AVEC puces gardent la détection de contenu ci-dessus.
  // gatsby-source-strapi expose une relation soit en tableau, soit en objet unique
  // selon le nombre d'éléments → on normalise systématiquement en tableau (le reste du
  // fichier fait déjà des `Array.isArray(...) ? [0] : ...` pour la même raison).
  const asArray = (a) => (a == null ? [] : Array.isArray(a) ? a : [a]);
  const firstOf = (a) => asArray(a)[0];
  const withCm = (d) => (d ? (/cm/i.test(d) ? d : `${d} cm`) : null);
  const withGm2 = (g) => (g ? (/g\s*\/\s*m|gr/i.test(g) ? g : `${g} g/m²`) : null);
  // Anses : donnée hétérogène. On ajoute « cm » UNIQUEMENT sur une longueur
  // numérique nue (ex. « 65 » → « 65 cm ») ; on laisse intacts les libellés
  // textuels (« anses longues ») et les valeurs déjà unitées.
  const withCmAnses = (a) => {
    if (!a) return null;
    const v = String(a).trim();
    if (/cm/i.test(v)) return v;
    return /^\d+([.,]\d+)?$/.test(v) ? `${v} cm` : v;
  };
  // Certains attributs sont saisis avec « _ » (tiret bas) comme séparateur
  // (ex. cabas-lola) : on le remplace par une virgule propre à l'affichage.
  const cleanSep = (s) => (s ? s.replace(/\s*_\s*/g, ", ").trim() : s);
  const structured = {
    dimensions: withCm(firstOf(product.attributs_dimensions)?.Dimension),
    composition: cleanSep(asArray(product.attributs_compositions).map((c) => c.Nom).filter(Boolean).join(", ")) || null,
    grammage: withGm2(firstOf(product.attributs_grammages)?.Nom),
    anses: withCmAnses(firstOf(product.attributs_anses)?.Nom),
    label: firstOf(product.attributs_labels)?.Label || null,
  };
  const src = hasBullets ? detected : structured;

  const productAttributes = [
    ...(src.dimensions ? [["Dimensions", src.dimensions]] : []),
    ...(src.composition ? [["Composition", src.composition]] : []),
    ...(src.grammage ? [["Grammage", src.grammage]] : []),
    ...(src.anses ? [["Anses", src.anses]] : []),
    ...(product.attributs_zone ? [["Zone d'impression",
      <span className="flex items-center justify-center gap-2">
        {product.attributs_zone.Name + ' cm'}
        {product.zone_image?.localFile && (
          <button
            onClick={() => setShowZoneImageModal(true)}
            className="ml-2 inline-flex items-center gap-1 whitespace-nowrap cursor-pointer hover:opacity-70 transition-opacity"
            aria-label="Voir l'image de la zone d'impression"
          > voir le gabarit
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
          </button>
        )}
      </span>
    ]] : []),
    ...(src.label ? [["Label", /oeko-?tex/i.test(String(src.label)) ? <>{src.label}<OekoTexSvg className="w-8 h-8" /></> : src.label]] : []),
    ...(hasBullets && autres.length ? [["Autres informations", autres.join(" · ")]] : []),
  ];

  useEffect(() => {
    console.log(color, marquage);
    if (color && marquage && marquage !== "Choisir une option") {
      setCurrentStep(2);
      setShowError(false);
    }
    else if (color) {
      setCurrentStep(1);
    } else {
      setCurrentStep(0);
    }
  }, [color, marquage]);

  return (
    <Layout seo={seo} showHero={false} productAdded={cartItem}>
      <div className="container my-6">
        <div className="flex flex-wrap">
          <div className="w-full md:w-6/12 md:pr-4">
            <div className="relative">
              {(() => {
                const grammage = Array.isArray(product.attributs_grammages)
                  ? product.attributs_grammages[0]
                  : product.attributs_grammages;
                const label = Array.isArray(product.attributs_labels)
                  ? product.attributs_labels[0]
                  : product.attributs_labels;
                return (
                  <div className="absolute top-12 left-0 z-10 flex flex-col gap-1 my-2 pointer-events-none">
                    {grammage && grammage.Nom && (
                      <span className="bg-blue-hover text-white text-2xl px-6 py-2 rounded-r">
                        {grammage.Nom} gr
                      </span>
                    )}
                    {label && label.Label === 'GOTS' && (
                      <span className="block bg-green-badge text-white text-2xl px-6 py-2 rounded-r">
                        BIO
                      </span>
                    )}
                  </div>
                );
              })()}
              {product.best_seller && (
                <div className="absolute top-0 right-0 w-80 h-48 z-10 pointer-events-none overflow-hidden text-center">
                  <span className="absolute block w-full text-white text-2xl uppercase py-1 bg-blue-hover shadow-sm" style={{ transform: "rotate(33deg)", right: "-60px", top: "50px" }}>
                    Best Seller
                  </span>
                </div>
              )}
              <Slider ref={slideRef} {...settings}>
                {images.map((image, index) => (
                  <GatsbyImage
                    key={index}
                    alt={image.alt}
                    image={getImage(image.image.localFile)}
                  />
                ))}
              </Slider>
            </div>
            <Slider ref={slideRef2} {...settings2}>
              {images.map((image, index) => (
                <div
                  key={index}
                  className="p-1"
                  onClick={(e) => {
                    slideRef.current.slickGoTo(index);
                    setColor(image.color);
                  }}
                >
                  <GatsbyImage
                    image={getImage(image.image.localFile)}
                    width="114"
                    height="140"
                    className="mr-1"
                    alt={image.alt}
                  />
                </div>
              ))}
            </Slider>
            {isPrintInFrance(category?.id) && (
              <div className="flex justify-center mt-6">
                <FrenchMade className="w-72" />
                {/* <div className="border-3 border-blue text-center p-4 mt-2">
                  <div className="m-0 text-lg font-semibold text-blue-hover uppercase">Le saviez-vous ?</div>
                  <p className="m-0 text-black">L'ensemble de nos sacs sont imprimés<br /> dans nos ateliers en france <FrenchFlag className="w-6 h-6 inline-block" /></p>
                </div> */}
              </div>
            )}

          </div>
          <div className="w-full md:w-6/12 md:pl-4">
            <form
              onSubmit={addToCart}
              name="ajouter-panier"
            >
              <Breadcrumbs category={category} product={product} disableClick={false} size="sm"></Breadcrumbs>
              <h1 className="text-left text-2xl font-semibold text-blue uppercase">{product.title}</h1>
              {/* <SeparatorComponent></SeparatorComponent> */}

              {/* <!-- Elfsight Google Reviews | page produit sous le titre --> */}
              <script src="https://static.elfsight.com/platform/platform.js" async></script>
              <div class="elfsight-app-c466fad9-a487-4cc9-97f2-acf1dd739b92" data-elfsight-app-lazy></div>

              <div className="product-description">

                {isPrintInFrance(category?.id) && (
                  <ul className="!pl-0 ml-0 flex justify-between space-x-2 text-xs" style={{ listStyleType: "none" }}>
                    <li className="rounded-xl bg-orangeLight px-2 w-full py-1">
                      <span className="flex space-x-1 text-black items-center justify-center">
                        <ImpressionFrancais className="w-4 h-4" />
                        <span>Impression en France</span>
                      </span>
                    </li>
                    <li className="rounded-xl bg-orangeLight px-2 w-full py-1">
                      <span className="flex space-x-1 text-black items-center justify-center">
                        <Livraison7 className="w-4 h-4" />
                        <span>Produit et expédié sous 7 à 10 jours ouvrés</span>
                      </span>
                    </li>
                    {/* <li className="rounded-xl bg-orangeLight px-2 w-full py-1">
                      <span className="flex space-x-1 text-black items-center justify-center">
                        <LivraisonOfferte className="w-4 h-4" />
                        <span>Livraison offerte</span>
                      </span>
                    </li> */}
                  </ul>
                )}
              </div>
              <div className="mt-4">
                {/* <div className="bg-orange uppercase text-center text-white py-1 px-2 my-4 ">
                  <span>Votre sac marqué et livré en 7 jours ouvrés</span>
                </div> */}
                <div className={`attributs-title  rounded-t-3xl ${currentStep === 0 ? 'active-step' : ''}`}>
                  <span>1. Choisissez la couleur de votre produit</span>
                </div>
                <div className="attribute-description">
                  <span className="">Sélectionnez une couleur : {color && color.Nom && color.Nom.replace(/[-_]/g, ' ')}</span>
                </div>
                <div className="border-1 border-gray-200 py-2 px-4 flex flex-wrap">
                  {product.variations &&
                    product.variations
                      .filter((v) => v.attributs_couleur)
                      .map((variation, index) => (
                        <span
                          title={variation.attributs_couleur.Nom}
                          key={index}
                          className={`block w-6 h-6 pt-1 mr-2 my-1 rounded-full shadow border-blue ${variation.attributs_couleur.Nom}`}
                          style={{
                            backgroundColor: variation.attributs_couleur.valeur,
                          }}
                          onClick={(e) => {
                            slideRef.current.slickGoTo(index + 1);
                            setColor(variation.attributs_couleur);
                            setNoStock(variation.no_stock === true);
                          }}
                        >
                          {data.check && slideIndex === index + 1 && (<GatsbyImage
                            className={`${slideIndex === index + 1
                              ? "block w-4 pt-1 ml-1"
                              : "hidden"
                              }`}
                            image={getImage(data.check)}
                            alt="Check"
                          ></GatsbyImage>)}
                        </span>
                      ))}
                  {noStock === true && (<span className="bg-gray-100 mt-2 p-1 text-sm text-red-danger text-center">Cette couleur reviendra prochainement en stock. Désolé pour ce désagrément.</span>)}

                </div>
              </div>
              <div>
                <div className={`attributs-title ${currentStep === 1 ? 'active-step' : ''}`}>
                  <span>2. Sélectionnez un type d'impression</span>
                </div>
                <div className="attribute-description">
                  <span>Choisir le marquage :</span>
                </div>
                <div className="marquage border-1 border-gray-200 py-2 px-4">
                  <select
                    onChange={(e) => {
                      setMarquage(e.target.value)
                    }}
                    className="p-2 bg-white border-1 border-gray-200 shadow-inner"
                  >
                    <option>Choisir une option</option>
                    {product.attributs_marquages
                      .sort((a, b) => a.Nom < b.Nom ? -1 : 1)
                      .filter(a => !color || !(/quadri/i.test(a.Nom) && (!/(ecru|blanc|beige|naturel)/i.test(color.Nom))))
                      .map((marquage, index) => (
                        <option key={index} value={marquage.Nom}>
                          {marquage.Nom}
                        </option>
                      ))}
                  </select>
                  <div className="flex space-x-6 items-center mt-2">
                    <div className="flex space-x-2 items-center" >
                      <input type="radio" className="w-4 mb-0 shadow-none" value={1} checked={faces === 1} onChange={updateFaces} id="1face" name="faces" />
                      <label htmlFor="1face">1 face</label>
                    </div>
                    <div className="flex space-x-2 items-center" >
                      <input type="radio" className="w-4 mb-0 shadow-none" value={2} checked={faces === 2} onChange={updateFaces} id="2face" name="faces" />
                      <label htmlFor="2face">2 faces</label>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className={`attributs-title ${currentStep === 2 ? 'active-step' : ''}`}>
                  <span>3. Choisir une quantité</span>
                </div>
                <div className="attribute-description">
                  <span>Commande de 100 pc. minimum :</span>
                </div>
                <div className="grid grid-rows-2 grid-cols-2 gap-0">
                  {quantities.map((qty, index) => (
                    <div
                      key={index}
                      className="flex space-x-2 border-1 border-gray-200 p-2 items-center"
                    >
                      <div className="w-4">
                        <input
                          onChange={updateQty}
                          checked={quantity === qty}
                          className="mb-0 h-6 shadow-none"
                          type="radio"
                          name="qty"
                          id={qty}
                          value={qty}
                        />
                      </div>
                      <label htmlFor={qty}>{qty}</label>
                    </div>
                  ))}
                </div>
                <div className="border-1 border-gray-200 py-2 px-4 flex space-x-4 items-center">
                  <span className="whitespace-nowrap">Autre quantité</span>
                  <input
                    name="quantity"
                    value={quantity}
                    onChange={updateQty}
                    type="number"
                    className="m-0 border-1 border-gray-200 shadow-inner"
                  />
                </div>
              </div>
              <div className="my-6 ">
                {noStock === true && (<span className="bg-gray-100 mt-2 p-1 text-sm text-red-danger text-center">Cette couleur reviendra prochainement en stock. Désolé pour ce désagrément.</span>)}
                {showError && (<div className="mt-2 p-1 text-sm text-red-danger text-center">Merci de choisir une couleur, une option d’impression et une quantité pour que nous puissions établir votre devis.</div>)}
                <button
                  onClick={(e) => {
                    if (!color || !marquage || quantity < 100) {
                      setShowError(true);
                      e.preventDefault();
                      return false;
                    }
                  }}
                  disabled={noStock}
                  className="w-full my-2 py-2 px-8 bg-blue rounded-3xl uppercase text-xl text-white disabled:opacity-50"
                >
                  Demandez un devis
                </button>

                <div className="text-center">
                  {/* <button
                    className="bg-blue bg-opacity-20 my-1 text-blue rounded-3xl px-6 py-2 font-semibold uppercase"
                    type="button"
                    onClick={() => setShowCall(true)}
                  >
                    Être rappelé
                  </button> */}

                  <span className="text-blue">Pour toute demande de devis urgente,<br />contactez-nous : </span>
                  <a
                    className=" my-1 bg-transparent py-2 text-blue font-bold underline"
                    href="mailto:devis@latelierdutotebag.com"
                  >
                    devis@latelierdutotebag.com
                  </a>

                </div>

              </div>
              {/* <div className="flex justify-between">
                <div className="flex flex-col items-center justify-center shadow-sm px-2 py-1">
                  <div className="flex items-center justify-center mb-2">
                    <Devis24h height="50" />

                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase text-center">
                    Devis en 24h
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center shadow-sm px-2 py-1">
                  <div className="flex items-center justify-center mb-2">
                    <Livraison7 height="50" />
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase text-center">
                    Expédié sous 7 à 10 jours
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center shadow-sm px-2 py-1">
                  <div className="flex items-center justify-center mb-2">
                    <LivraisonOfferte height="50" />
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase text-center">
                    Livraison offerte
                  </span>
                </div>
              </div> */}
            </form>

            <form id="call-me" name="se-faire-rappeler" data-netlify="true" action="/remerciements" netlify-honeypot="potdemiel" className={showCall === true ? 'block' : 'hidden'}>
              <input type="hidden" name="form-name" value="se-faire-rappeler" />
              <input type="hidden" name="utm_campaign" value={utm_campaign || ""} />
              <input type="hidden" name="utm_term" value={utm_term || ""} />
              <input type="hidden" name="gad_source" value={gad_source || "0"} />
              <p className="hidden">
                <label>Ne remplissez pas ce champ si vous êtes un humain: <input name="potdemiel" /></label>
              </p>
              <div className="flex items-end space-x-4">
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-500">Renseignez votre numéro de téléphone*</label>
                  <input name="phone" type="text" className="form-control mb-0" />
                </div>
                <div>
                  <button className="btn btn-blue uppercase ">On m'appelle</button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-8 mb-8">
          <h2 className="text-xl font-semibold text-blue uppercase text-center block">Description</h2>
          <hr className="border-2 border-blue" id="totebag-informations" />
        </div>
        <dl className="grid grid-cols-2 gap-y-2 text-center text-blue striped">

          {productAttributes.map((attribute, index) => (
            <div className="contents font-semibold" key={index}>
              <dt>{attribute[0]}</dt>
              <dd className="flex items-center justify-center">{attribute[1]}</dd>
            </div>
          ))}

          {/* <div className="contents">
            <dt>Couleurs</dt>
            <dd>{couleurs.map((c) => c.replace("-", " ")).join(", ")}</dd>
          </div>

          {attributes &&
            attributes
              // .join([])
              .map((attribute, index) => (
                <div className="contents" key={index}>
                  <dt>{attribute[0]}</dt>
                  <dd>{attribute[2] && attribute[2] === 1
                    ? product[attribute[1]].map((v, i, a) =>
                      i === a.length - 1 ? v.Nom : v.Nom + ", "
                    )
                    : product[attribute[1]] && product[attribute[1]].Nom}</dd>
                </div>
              ))} */}
        </dl>

        {descriptionProduit && (
          <section className="mt-8 mb-8 text-blue">
            <hr className="border-2 border-blue mb-6" />
            {descriptionProduit.titre && (
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">{descriptionProduit.titre}</h2>
            )}
            {descriptionProduit.paragraphes.map((paragraphe, index) => (
              <p className="mb-4" key={index}>{paragraphe}</p>
            ))}
          </section>
        )}

        {/* <div className="flex">
          <div className="md:w-3/12">
            <ul>
              <li
                className={`p-3 border-b-1 border-gray-200 ${activeTab === 0 ? "border-r-3" : ""
                  }`}
              >
                <a
                  href="#totebag-informations"
                  onClick={() => showTab(0)}
                  className={`text-sm text-gray-500 uppercase font-semibold ${activeTab === 0 ? "text-blue underline" : ""
                    }`}
                >
                  Description
                </a>
              </li>
              <li
                className={`p-3 border-b-1 border-gray-200 ${activeTab === 1 ? "border-r-3" : ""
                  }`}
              >
                <a
                  href="#totebag-informations"
                  onClick={() => showTab(1)}
                  className={`text-sm text-gray-500 uppercase font-semibold ${activeTab === 1 ? "text-blue underline" : ""
                    }`}
                >
                  Informations complémentaires
                </a>
              </li>
            </ul>
          </div>
          <div className="md:w-9/12 border-l-1 border-gray-200 pl-4">
            <div className={activeTab === 0 ? "block" : "hidden"}>
              <Markdown>{product.short_description}</Markdown>
            </div>
            <div className={activeTab === 1 ? "block" : "hidden"}>
              <div className="flex space-x-6 py-2 border-b-1 border-gray-200">
                <div className="w-3/12">
                  <span className="font-semibold text-sm">Couleurs</span>
                </div>
                <div className="w-9/12 flex space-x-2">
                  <span className="text-sm capitalize">
                    {couleurs
                      .map((c) => c.replace("-", " "))
                      .map((c, i, a) => (i === a.length - 1 ? c : c + ", "))}
                  </span>
                </div>
              </div>
              {attributes &&
                attributes.map((attribute, index) => (
                  <div
                    key={index}
                    className="flex space-x-6 py-2 border-b-1 border-gray-200"
                  >
                    <div className="w-3/12">
                      <span className="font-semibold text-sm">
                        {attribute[0]}
                      </span>
                    </div>
                    <div className="w-9/12 flex space-x-2">
                      <span className="text-sm">
                        {attribute[2] && attribute[2] === 1
                          ? product[attribute[1]].map((v, i, a) =>
                            i === a.length - 1 ? v.Nom : v.Nom + ", "
                          )
                          : product[attribute[1]] && product[attribute[1]].Nom}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div> */}

        <div>
          <script src="https://static.elfsight.com/platform/platform.js" async></script>
          <div className="elfsight-app-85fa1994-851a-4f7e-bf6e-92714d6b1669" data-elfsight-app-lazy></div>
        </div>

        <hr className="my-8" />

        <FAQProductView questionProduit={questionProduit} />

        <div>
          <h2 className="uppercase font-semibold ">Produits apparentés</h2>
          <ProductList products={products} cols={4}></ProductList>
        </div>
      </div>
      <SkeepersWidget id="d0f9cc4e-4647-495b-a12a-9dab4e166c8f" />

      {/* Modal pour afficher l'image de la zone d'impression */}
      {showZoneImageModal && product.zone_image?.localFile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setShowZoneImageModal(false)}
        >
          <div
            className="relative max-w-4xl w-full bg-white rounded-lg p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowZoneImageModal(false)}
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-2xl font-bold"
              aria-label="Fermer"
            >
              ×
            </button>
            <div className="mt-4">
              <GatsbyImage
                image={getImage(product.zone_image.localFile)}
                alt="Zone d'impression"
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}
    </Layout >
  );
});


export default Product;
